import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { eyeIcon, eyeSlashIcon } from '@progress/kendo-svg-icons';
import { currentLocation } from '../../../assets/current-location'
import 'azure-maps-drawing-tools/dist/atlas-drawing.min.css';
import { getLikelihoodColor } from '../../../config/likelihoodColors';
import { loadingPanel } from '../../LoadingPanel';

type GeoVertex = { longitude: number; latitude: number };
type PathPoint = {
  lon: number;
  lat: number;
  color?: string;
  date?: string;
  lastPositionDatetime?: string;
  localTime?: string;
  likelihood?: number;
};
type PathSeries = {
  deviceMac: string;
  pathColor: string;
  points: [number, number][];
  entries: PathPoint[];
};
type PathMarkerState = {
  position: [number, number];
  pathColor: string;
  deviceMac: string;
  pointData: PathPoint;
  pointIndex: number;
  signalsCount: number;
};

const PATH_COLOR_PALETTE = ['#198692', '#ef6c00', '#1565c0', '#2e7d32', '#c62828', '#6d4c41'];


export const AzureMapSpaghettiChart = (props: any) => {
  const mapContainer = useRef(null);
  const mapRef = useRef<atlas.Map | null>(null);

  const localizationService = useLocalization();
  const languageId: any = localStorage.getItem('languageId');

  const [mapReady, setMapReady] = useState(false);
  const [hiddenDevices, setHiddenDevices] = useState<Record<string, boolean>>({});

  const [pathStep, setPathStep] = useState(0);
  const isValidLongitude = (value: number) => value >= -180 && value <= 180;
  const isValidLatitude = (value: number) => value >= -90 && value <= 90;
  const fallbackCenter: [number, number] = [12.4964, 41.9028]; //Rome

  type CameraMode = 'idle' | 'auto' | 'reset';

  const cameraRef = useRef<{
    initialized: boolean;
    mode: CameraMode;
    lastKey: string;
    baseCenter: [number, number];
  }>({
    initialized: false,
    mode: 'idle',
    lastKey: '',
    baseCenter: fallbackCenter
  });


  const {
    geoStaticToggle,
    map,
    zones,
    coordinatesList,
    formattedIntervalDate,
    fromDate,
    toDate,
    countWakeups,
    showColor = false,
    showSlider,
    externalSliderStartPercent,
    externalSliderPercent,
    analysisKey,
    onSliderDateChange,
    showAllPoints,
    selectedDevices,
    deviceNamesByMac = {},
    distanceByDevice = {},
    onPolygonVerticesChange,
    signalsGrouping
  } = props;
  const selectedDevicesLabel = Array.isArray(selectedDevices) ? selectedDevices.join(', ') : (selectedDevices ?? '');
  const nameLabel = languageId === 'it-IT' ? 'Nome' : 'Name';
  const currentLabel = languageId === 'it-IT' ? 'Attuale' : 'Current';
  const signalLabel = languageId === 'it-IT' ? 'Segnale' : 'Signal';

  const hasExternalSliderControl = Number.isFinite(Number(externalSliderPercent));

  const normalizedFromDate = typeof fromDate === 'string' ? fromDate.trim() : '';
  const normalizedFormattedIntervalDate = typeof formattedIntervalDate === 'string' ? formattedIntervalDate.trim() : '';
  const normalizedToDate = typeof toDate === 'string' ? toDate.trim() : '';

  const sliderFromDate = normalizedFromDate || normalizedFormattedIntervalDate || '';
  const sliderToDate = normalizedToDate || new Date().toLocaleString(languageId || undefined);

  let currentMap: any = map;
  if (currentMap == null) {
    try {
      currentMap = JSON.parse(localStorage.getItem('mapSelected') || '{}');
    } catch {
      currentMap = {};
    }
  }

  const roundDownTo3Decimals = (value: number) => Math.floor(value * 10) / 10;
  const roundUpTo3Decimals = (value: number) => Math.ceil(value * 10) / 10;

  const mapMaxBounds = useMemo<[number, number, number, number] | undefined>(() => {
    if (!Array.isArray(coordinatesList) || coordinatesList.length === 0) {
      return undefined;
    }

    const points = coordinatesList.flatMap((line: any) => {
      const rawPoints = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;

      if (!Array.isArray(rawPoints)) {
        return [];
      }

      return rawPoints
        .map((point: any) => {
          if (Array.isArray(point) && point.length >= 2) {
            const latitude = Number(point[0]);
            const longitude = Number(point[1]);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              return null;
            }

            return { longitude, latitude };
          }

          const latitude = Number(point?.lat ?? point?.latitude);
          const longitude = Number(point?.long ?? point?.longitude ?? point?.lon);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }

          return { longitude, latitude };
        })
        .filter((point): point is { longitude: number; latitude: number } => point !== null);
    });

    const cornerPoints = [currentMap?.botLeft, currentMap?.botRight, currentMap?.topLeft, currentMap?.topRight]
      .map((corner: any) => ({ longitude: Number(corner?.longitude), latitude: Number(corner?.latitude) }))
      .filter((corner) => Number.isFinite(corner.longitude) && Number.isFinite(corner.latitude));

    // The map area must stay reachable so the viewport can be fitted to it.
    points.push(...cornerPoints);

    if (!points.length) {
      return undefined;
    }

    const minLongitude = roundDownTo3Decimals(Math.min(...points.map((point) => point.longitude)));
    const minLatitude = roundDownTo3Decimals(Math.min(...points.map((point) => point.latitude)));
    const maxLongitude = roundUpTo3Decimals(Math.max(...points.map((point) => point.longitude)));
    const maxLatitude = roundUpTo3Decimals(Math.max(...points.map((point) => point.latitude)));

    if (minLongitude >= maxLongitude || minLatitude >= maxLatitude) {
      return undefined;
    }

    return [minLongitude, minLatitude, maxLongitude, maxLatitude];
  }, [coordinatesList, currentMap]);

  const zonesList = zones;

  const [fromDateUpdated, setFromDateUpdated] = useState<string>('')
  const [toDateUpdated, setToDateUpdated] = useState<string>('')

  const polygonVerticesCallbackRef = useRef<((vertices: GeoVertex[]) => void) | undefined>(onPolygonVerticesChange);
  useEffect(() => {
    polygonVerticesCallbackRef.current = onPolygonVerticesChange;
  }, [onPolygonVerticesChange]);

  const toValidCenter = (candidate: any): [number, number] | null => {
    if (!Array.isArray(candidate) || candidate.length < 2) {
      return null;
    }

    const longitude = Number(candidate[0]);
    const latitude = Number(candidate[1]);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
      return null;
    }

    return [longitude, latitude];
  };

  const normalizedPathSeries = useMemo<PathSeries[]>(() => {
    if (!Array.isArray(coordinatesList)) {
      return [];
    }

    return coordinatesList
      .map((line: any, seriesIndex: number) => {
        const rawCoordList = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;
        if (!Array.isArray(rawCoordList)) {
          return null;
        }

        const entries = rawCoordList
          .map((item: any): PathPoint | null => {
            if (Array.isArray(item) && item.length >= 2) {
              const latitude = Number(item[0]);
              const longitude = Number(item[1]);

              if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
                return null;
              }

              return {
                lon: longitude,
                lat: latitude,
                color: item[2],
                date: item[3],
                lastPositionDatetime: item[4],
                localTime: item[6],
                likelihood: Number(item[5])
              };
            }

            const latitude = Number(item?.lat ?? item?.latitude);
            const longitude = Number(item?.long ?? item?.longitude ?? item?.lon);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              return null;
            }

            return {
              lon: longitude,
              lat: latitude,
              color: item?.color,
              date: item?.datetime ?? item?.date,
              lastPositionDatetime: item?.lastPositionDatetime,
              localTime: item?.localTime,
              likelihood: Number(item?.likelihood)
            };
          })
          .filter((entry: PathPoint | null): entry is PathPoint => entry !== null);

        if (!entries.length) {
          return null;
        }

        const lineDeviceMac = typeof line?.deviceMac === 'string' && line.deviceMac.trim() !== ''
          ? line.deviceMac
          : (selectedDevices?.[seriesIndex] || `Device ${seriesIndex + 1}`);

        const selectedDeviceIndex = Array.isArray(selectedDevices)
          ? selectedDevices.findIndex((mac: string) => mac === lineDeviceMac)
          : -1;

        const paletteIndex = selectedDeviceIndex >= 0 ? selectedDeviceIndex : seriesIndex;

        return {
          deviceMac: lineDeviceMac,
          pathColor: PATH_COLOR_PALETTE[paletteIndex % PATH_COLOR_PALETTE.length],
          points: entries.map((entry) => [entry.lon, entry.lat] as [number, number]),
          entries
        };
      })
      .filter((series: PathSeries | null): series is PathSeries => series !== null);
  }, [coordinatesList, selectedDevices]);

  const visiblePathSeries = useMemo<PathSeries[]>(
    () => normalizedPathSeries.filter((series) => !hiddenDevices[series.deviceMac]),
    [normalizedPathSeries, hiddenDevices]
  );

  const cleanedCoordinates = useMemo<[number, number][]>(() => {
    return visiblePathSeries[0]?.points ?? [];
  }, [visiblePathSeries]);

  const infoDevice = useMemo<any[][]>(() => {
    return visiblePathSeries[0]?.entries.map((entry) => [
      entry.lon,
      entry.lat,
      entry.color,
      entry.date,
      entry.lastPositionDatetime,
      entry.likelihood
    ]) ?? [];
  }, [visiblePathSeries]);

  const maxPathLength = useMemo(() => {
    return visiblePathSeries.reduce((maxLength, series) => Math.max(maxLength, series.points.length), 0);
  }, [visiblePathSeries]);

  const buildPathSegmentFeatures = useCallback((minIndex: number, maxIndex: number) => {
    const features: atlas.data.Feature<atlas.data.LineString, any>[] = [];

    visiblePathSeries.forEach((series) => {
      const seriesMinIndex = Math.max(0, Math.min(minIndex, series.points.length - 1));
      const seriesMaxIndex = Math.min(maxIndex, series.points.length - 1);

      for (let i = Math.max(1, seriesMinIndex + 1); i <= seriesMaxIndex; i++) {
        const start = series.points[i - 1];
        const end = series.points[i];

        if (!start || !end) {
          continue;
        }

        const likelihood = Number(series.entries?.[i]?.likelihood);
        const segmentColor = showColor
          ? getLikelihoodColor(Number.isFinite(likelihood) ? likelihood : 0)
          : series.pathColor;

        features.push(
          new atlas.data.Feature(
            new atlas.data.LineString([start, end]),
            { color: segmentColor, deviceMac: series.deviceMac }
          )
        );
      }
    });

    return features;
  }, [visiblePathSeries, showColor]);

  const setCameraSafe = useCallback((options: atlas.CameraOptions, mode: CameraMode) => {
    const map = mapRef.current;
    if (!map) return;

    const cam = cameraRef.current;

    // ✅ salva centro base SOLO la prima volta
    if (!cam.initialized && options.center) {
      cam.baseCenter = options.center as [number, number];
      cam.initialized = true;
    }

    cam.mode = mode;

    map.setCamera(options);
  }, []);

  const derivedMapCenter = useMemo<[number, number] | null>(() => {
    const west = Number(coordinatesList?.botLeft?.longitude);
    const east = Number(coordinatesList?.botRight?.longitude);
    const south = Number(coordinatesList?.botLeft?.latitude);
    const north = Number(coordinatesList?.topLeft?.latitude);

    if (![west, east, south, north].every(Number.isFinite)) {
      return null;
    }

    const longitude = (west + east) / 2;
    const latitude = (south + north) / 2;

    return toValidCenter([longitude, latitude]);
  }, [currentMap]);


  useEffect(() => {
    if (!mapRef.current) return;
    if (!derivedMapCenter) return;

    const cam = cameraRef.current;

    if (cam.initialized) return;

  }, [derivedMapCenter]);

  const getSelectedZones = () => {
    const zonesToProcess = props.subscription ? [zonesList] : zonesList || [];
    const selectedZone: any[] = [];
    const zonesLatLngList: any[] = [];

    if (zonesToProcess.length > 0) {
      zonesToProcess?.forEach((zone: any) => {
        if (zone?.id !== 0) {
          const zoneColor = typeof zone.color === 'string' && zone.color.trim() ? zone.color : '#ff0000';
          const coordinates = zone.points?.map((point: any) => [point.longitude, point.latitude]) || [];

          // Se ci sono coordinate, aggiungiamo la prima alla fine
          if (coordinates.length > 0) {
            coordinates.push(coordinates[0]);
          }

          const newZone = {
            type: 'Feature',
            properties: { fillColor: zoneColor },
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates],
            },
          };

          selectedZone.push(newZone);
          zonesLatLngList.push(coordinates);
        }
      });
    } else {
      const zoneColor = typeof zonesToProcess.color === 'string' && zonesToProcess.color.trim() ? zonesToProcess.color : '#ff0000';
      const coordinates = zonesToProcess.points?.map((point: any) => [point.longitude, point.latitude]) || [];

      // Se ci sono coordinate, aggiungiamo la prima alla fine
      if (coordinates.length > 0) {
        coordinates.push(coordinates[0]);
      }

      const newZone = {
        type: 'Feature',
        properties: { fillColor: zoneColor },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      };

      selectedZone.push(newZone);
      zonesLatLngList.push(coordinates);
    }

    return { selectedZone, zonesLatLngList };
  };

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    setMapReady(false);

    const onMapReady = () => {
      setMapReady(true);
    };

    map.events.add('ready', onMapReady);

    return () => {
      map?.events.remove('ready', onMapReady);
    };
  }, [mapRef?.current]);

  useEffect(() => {
    if (!mapContainer.current) return;

    setMapReady(false);

    const map = new atlas.Map(mapContainer.current, {
      style: 'satellite_road_labels',
      center: derivedMapCenter ?? fallbackCenter,
      zoom: 10,
      maxZoom: 20,
      maxBounds: mapMaxBounds,
      language: 'it-IT',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: process.env.REACT_APP_AZURE_MAPS_KEY,
      },
      attributionControl: false,
    });

    mapRef.current = map;
    map.events.add('ready', () => {

      // Salva lo stile quando l'utente lo cambia
      map.events.add('stylechanged', () => {
        const currentStyle: any = map.getStyle().style;
        localStorage.setItem('mapStyle', currentStyle); // Salva lo stile in localStorage
      });

      map.controls.add([new atlas.control.ZoomControl(), new atlas.control.FullscreenControl()], {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.StyleControl({
        mapStyles: ['satellite_road_labels', 'satellite', 'road'],
        layout: 'icons'
      }), {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.ScaleControl({ unit: 'metric' }), {
        position: atlas.ControlPosition.TopRight
      });

      const source = new atlas.source.DataSource();
      map.sources.add(source);
      setMapReady(true);

    });

    return () => map?.dispose();
  }, []);

  //manage zones
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !mapContainer.current) {
      return;
    }

    const zoneLayerId = 'zone-polygon-layer';
    const zoneSourceId = 'zone-source';

    const removeZoneArtifacts = () => {
      const existingLayer = map.layers.getLayerById(zoneLayerId);
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }

      const existingSource = map.sources.getById(zoneSourceId);
      if (existingSource) {
        map.sources.remove(existingSource);
      }
    };

    if (!zonesList) {
      removeZoneArtifacts();
      return;
    }

    const applyZones = () => {
      if (!map.sources || !map.layers) {
        return;
      }

      removeZoneArtifacts();

      const hasZones = Array.isArray(zonesList) ? zonesList.length > 0 : Boolean(zonesList);
      if (!hasZones) {
        return;
      }

      const { selectedZone } = getSelectedZones();

      const dataSource: any = new atlas.source.DataSource(zoneSourceId);
      map.sources.add(dataSource);

      dataSource.add({
        type: 'FeatureCollection',
        features: selectedZone
      });

      const polygonLayer = new atlas.layer.PolygonLayer(dataSource, zoneLayerId, {
        fillColor: ['coalesce', ['get', 'fillColor'], 'transparent'],
        fillOpacity: 0.5,
      });

      map.layers.add(polygonLayer);
    };

    if ((map as any)?.ready) {
      applyZones();
      return;
    }

    const readyHandler = () => {
      applyZones();
    };

    map.events.addOnce('ready', readyHandler);

    return () => {
      map.events.remove('ready', readyHandler);
    };
  }, [zonesList, mapReady]);

  //geoStaticToggle
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map) return;

    // Rimuove le zone (layer + source)
    const zoneLayer = map.layers.getLayerById('zone-polygon-layer');
    if (zoneLayer) {
      map.layers.remove(zoneLayer);
    }

    const zoneSource = map.sources.getById('zone-source');
    if (zoneSource) {
      map.sources.remove(zoneSource);
    }

    // Stato zone selezionate
  }, [geoStaticToggle]);


  const resetCenter = () => {
    const cam = cameraRef.current;

    cam.mode = 'reset';
    cam.lastKey = '';

    if (mapVerticesBoundsKey) {
      setCameraSafe({
        bounds: mapVerticesBoundsKey.split(',').map(Number) as [number, number, number, number],
        padding: 30
      }, 'reset');
      return;
    }

    setCameraSafe({
      center: cam.baseCenter ?? fallbackCenter,
      zoom: 15.75
    }, 'reset');
  };

  const lineDataSourceRef = useRef<any>(null);
  const markerRefs = useRef<atlas.HtmlMarker[]>([]);
  const popupRef = useRef<any>(null);
  const previousPathLengthRef = useRef<number>(0);
  const allPointsSourceRef = useRef<atlas.source.DataSource | null>(null);
  const allPointsLayerRef = useRef<atlas.layer.BubbleLayer | null>(null);
  const allPointsPopupRef = useRef<atlas.Popup | null>(null);
  const allPointsRenderTokenRef = useRef(0);
  const allPointsFrameRef = useRef<number | null>(null);

  const [allPointsLoading, setAllPointsLoading] = useState(false);

  const clearPathMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !markerRefs.current.length) {
      return;
    }

    markerRefs.current.forEach((marker) => {
      map.markers.remove(marker);
    });

    markerRefs.current = [];
  }, []);

  const signalsByDevice = useMemo(() => {
    return visiblePathSeries.reduce((acc: Record<string, number>, series) => {
      acc[series.deviceMac] = (acc[series.deviceMac] || 0) + series.entries.length;
      return acc;
    }, {});
  }, [visiblePathSeries]);

  const getMarkerStates = useCallback((index: number) => {
    return visiblePathSeries
      .map((series): PathMarkerState | null => {
        if (!series.points.length) {
          return null;
        }

        const markerIndex = Math.max(0, Math.min(index, series.points.length - 1));
        const pointData = series.entries[markerIndex];

        return {
          position: series.points[markerIndex],
          pathColor: series.pathColor,
          deviceMac: series.deviceMac,
          pointData,
          pointIndex: markerIndex,
          signalsCount: signalsByDevice[series.deviceMac] ?? series.entries.length
        };
      })
      .filter((state): state is PathMarkerState => state !== null);
  }, [visiblePathSeries, signalsByDevice]);

  //  fit the viewport to the map vertices once per analysis (never when toggling device visibility)
  const mapVerticesBounds = useMemo<[number, number, number, number] | null>(() => {
    const corners = [currentMap?.botLeft, currentMap?.botRight, currentMap?.topLeft, currentMap?.topRight]
      .map((corner: any) => [Number(corner?.longitude), Number(corner?.latitude)] as [number, number])
      .filter(([lon, lat]) => Number.isFinite(lon) && Number.isFinite(lat) && isValidLongitude(lon) && isValidLatitude(lat));

    if (corners.length < 2) {
      return null;
    }

    const lons = corners.map(([lon]) => lon);
    const lats = corners.map(([, lat]) => lat);
    const bounds: [number, number, number, number] = [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];

    return bounds[0] < bounds[2] && bounds[1] < bounds[3] ? bounds : null;
  }, [currentMap]);

  const mapVerticesBoundsKey = mapVerticesBounds ? mapVerticesBounds.join(',') : '';
  const lastFittedViewportKeyRef = useRef('');

  useEffect(() => {
    if (!mapReady || !mapVerticesBoundsKey || analysisKey === undefined) return;

    const map = mapRef.current;
    if (!map) return;

    const viewportKey = `${analysisKey}:${mapVerticesBoundsKey}`;
    if (lastFittedViewportKeyRef.current === viewportKey) return;
    lastFittedViewportKeyRef.current = viewportKey;

    setCameraSafe({
      bounds: mapVerticesBoundsKey.split(',').map(Number) as [number, number, number, number],
      padding: 30,
      type: 'jump'
    }, 'auto');
  }, [analysisKey, mapReady, mapVerticesBoundsKey, setCameraSafe]);

  // manage path_history
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map || !mapContainer.current || !map.sources) return;

    if (!maxPathLength) {
      // Every device hidden: wipe the previously drawn traces and markers.
      lineDataSourceRef.current?.clear();
      clearPathMarkers();
      return;
    }

    const addLineLayer = () => {

      const existingLayer = map.layers.getLayerById('line-layer');
      if (existingLayer) map.layers.remove(existingLayer);

      const existingSource = map.sources.getById('line-source');
      if (existingSource) map.sources.remove(existingSource);

      const dataSource = new atlas.source.DataSource('line-source');
      map.sources.add(dataSource);

      const features = buildPathSegmentFeatures(0, maxPathLength - 1);
      if (features.length > 0) {
        dataSource.add(features as any);
      }

      const lineLayer = new atlas.layer.LineLayer(dataSource, 'line-layer', {
        strokeColor: ['get', 'color'],
        strokeWidth: 3
      });

      map.layers.add(lineLayer);
      lineDataSourceRef.current = dataSource;
    };

    try {
      addLineLayer();
    } catch {
      // in caso di errore, aspetta comunque "ready"
    }

    if (showSlider || hasExternalSliderControl) {
      const lastPathIndex = Math.max(cleanedCoordinates.length - 1, 0);

      clearPathMarkers();

      const markerStates = getMarkerStates(lastPathIndex);
      markerRefs.current = markerStates.map((state) => {
        const marker = createPathHistoryMarker(state.position, state.pathColor);
        map.markers.add(marker);
        return marker;
      });

      updateLines(0, lastPathIndex);
      setPathStep(lastPathIndex);

    }
    else {
      clearPathMarkers();
    }

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -30], closeButton: false });
    }

    initMarkerPopup(markerRefs.current[0] || null);

    // clean
    return () => {
      clearPathMarkers();
    };

  }, [mapReady, maxPathLength, cleanedCoordinates, showSlider, hasExternalSliderControl, showColor, buildPathSegmentFeatures, clearPathMarkers, getMarkerStates]);

  const alignedData = useMemo(() => {
    if (!infoDevice?.length) return [];
    return infoDevice.map((d: any) => ({
      coord: [d[0], d[1]], // [lon, lat]
      date: d[3],
      lastPositionDatetime: d[4],
      localTime: d[6],
      likelihood: d[5]
    }));
  }, [infoDevice]);

  const allAlignedData = useMemo(() => {
    return visiblePathSeries.flatMap((series, seriesIndex) =>
      series.entries.map((entry, pointIndex) => ({
        coord: [entry.lon, entry.lat],
        date: entry.date,
        lastPositionDatetime: entry.lastPositionDatetime,
        localTime: entry.localTime,
        likelihood: entry.likelihood,
        deviceMac: series.deviceMac,
        pathColor: series.pathColor,
        seriesIndex,
        pointIndex
      }))
    );
  }, [visiblePathSeries]);

  const pathLegendItems = useMemo(() => {
    const seen = new Set<string>();

    return normalizedPathSeries
      .filter((series) => {
        if (seen.has(series.deviceMac)) {
          return false;
        }

        seen.add(series.deviceMac);
        return true;
      })
      .map((series) => ({
        deviceMac: series.deviceMac,
        pathColor: series.pathColor
      }));
  }, [normalizedPathSeries]);

  const parseDateLabel = useCallback((value?: any) => {
    if (!value || typeof value !== 'string') {
      return value || '-';
    }

    const normalized = value.trim();
    const dmyMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (dmyMatch) {
      const [, dd, mm, yyyy, hh = '0', min = '0', ss = '0'] = dmyMatch;
      const parsedUtc = new Date(Date.UTC(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(min),
        Number(ss),
        0
      ));

      if (!Number.isNaN(parsedUtc.getTime())) {
        return parsedUtc.toLocaleString();
      }
    }

    const fallback = new Date(normalized);
    return Number.isNaN(fallback.getTime()) ? normalized : fallback.toLocaleString();
  }, []);

  const escapeTooltipHtml = useCallback((value: any) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }, []);

  const buildPathHistoryTooltipHtml = useCallback((params: {
    title: string;
    accentColor?: string;
    rows: Array<{ label: string; value: string; mono?: boolean }>;
  }) => {
    const accentColor = params.accentColor || '#198692';
    const rowsHtml = params.rows
      .map((row) => `<div class="device-tooltip__row"><dt>${escapeTooltipHtml(row.label)}</dt><dd>${row.mono ? `<span class="device-tooltip__mono">${escapeTooltipHtml(row.value)}</span>` : escapeTooltipHtml(row.value)}</dd></div>`)
      .join('');

    return `<div class="device-tooltip" style="--device-tooltip-accent:${accentColor};">
      <div class="device-tooltip__header">
        <span class="device-tooltip__swatch" aria-hidden="true"></span>
        <span class="device-tooltip__title">${escapeTooltipHtml(params.title)}</span>
      </div>
      <dl class="device-tooltip__body">${rowsHtml}</dl>
    </div>`;
  }, [escapeTooltipHtml]);

  const seriesSummaryByDevice = useMemo(() => {
    const summary: Record<string, {
      name: string;
      mac: string;
      signalsCount: number;
      distance: string;
      firstDate: string;
      lastDate: string;
      color: string;
    }> = {};

    normalizedPathSeries.forEach((series) => {
      const entries = series.entries || [];
      const firstEntry = entries[0];
      const lastEntry = entries[entries.length - 1];
      const distanceRaw = distanceByDevice?.[series.deviceMac];

      summary[series.deviceMac] = {
        name: deviceNamesByMac?.[series.deviceMac] || '-',
        mac: series.deviceMac,
        signalsCount: entries.length,
        distance: distanceRaw ? String(distanceRaw) : '',
        firstDate: parseDateLabel(firstEntry?.date),
        lastDate: parseDateLabel(lastEntry?.lastPositionDatetime || lastEntry?.date),
        color: series.pathColor
      };
    });

    return summary;
  }, [normalizedPathSeries, distanceByDevice, deviceNamesByMac, parseDateLabel]);

  useEffect(() => {
    setHiddenDevices((prev) => {
      const next: Record<string, boolean> = {};
      pathLegendItems.forEach((item) => {
        if (prev[item.deviceMac]) {
          next[item.deviceMac] = true;
        }
      });
      return next;
    });
  }, [pathLegendItems]);

  const toggleDeviceVisibility = useCallback((deviceMac: string) => {
    setHiddenDevices((prev) => ({
      ...prev,
      [deviceMac]: !prev[deviceMac]
    }));
  }, []);

  const clearAllPointsLayer = useCallback(() => {
    const map = mapRef.current;

    allPointsRenderTokenRef.current += 1;

    if (allPointsFrameRef.current !== null) {
      cancelAnimationFrame(allPointsFrameRef.current);
      allPointsFrameRef.current = null;
    }

    setAllPointsLoading(false);

    if (!map) {
      return;
    }

    if (allPointsLayerRef.current) {
      map.layers.remove(allPointsLayerRef.current);
      allPointsLayerRef.current = null;
    }

    if (allPointsSourceRef.current) {
      map.sources.remove(allPointsSourceRef.current);
      allPointsSourceRef.current = null;
    }

    if (allPointsPopupRef.current) {
      allPointsPopupRef.current.close();
    }
  }, []);

  const showAllMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !allAlignedData.length) {
      clearAllPointsLayer();
      return;
    }

    clearAllPointsLayer();
    setAllPointsLoading(true);

    const renderToken = allPointsRenderTokenRef.current;
    const sourceId = 'path-history-all-points-source';
    const layerId = 'path-history-all-points-layer';
    const source = new atlas.source.DataSource(sourceId);
    const layer = new atlas.layer.BubbleLayer(source, layerId, {
      color: ['coalesce', ['get', 'pointColor'], '#198692'],
      radius: 6,
      strokeColor: 'white',
      strokeWidth: 2,
      opacity: 0.95
    });

    const popup = allPointsPopupRef.current ?? new atlas.Popup({ pixelOffset: [0, -30], closeButton: false });
    allPointsPopupRef.current = popup;

    map.sources.add(source);
    map.layers.add(layer);

    allPointsSourceRef.current = source;
    allPointsLayerRef.current = layer;

    map.events.add('mouseover', layer, (event: any) => {
      const shape = event?.shapes?.[0];
      const coordinates = shape?.getCoordinates?.() ?? shape?.geometry?.coordinates;
      if (!coordinates) {
        return;
      }

      const props = shape?.getProperties?.() ?? {};
      const pointDate = props.date || '';
      const pointLocalTime = props.localTime || '';
      const pointDeviceMac = props.deviceMac || selectedDevicesLabel;
      const signalsForDevice = signalsByDevice[pointDeviceMac] ?? countWakeups;
      const accuracy = Number(props.likelihood);
      const normalizedAccuracy = Number.isFinite(accuracy) ? accuracy.toFixed(2) : '';
      const summary = seriesSummaryByDevice[pointDeviceMac];

      popup.setOptions({
        position: coordinates,
        content: buildPathHistoryTooltipHtml({
          title: summary?.name && summary.name !== '-' ? summary.name : pointDeviceMac,
          accentColor: summary?.color || props.pointColor,
          rows: [
            { label: nameLabel, value: summary?.name || '-' },
            { label: localizationService.toLanguageString('configuration.analysis.path_history.mac', 'Mac'), value: pointDeviceMac, mono: true },
            { label: signalLabel, value: `${Number(props.pointIndex) + 1}/${signalsForDevice}` },
            { label: localizationService.toLanguageString('configuration.analysis.path_history.distance', 'Distance'), value: summary?.distance || '-' },
            { label: localizationService.toLanguageString('configuration.analysis.path_history.first', 'First'), value: summary?.firstDate || '-' },
            { label: currentLabel, value: parseDateLabel(pointLocalTime) },
            { label: localizationService.toLanguageString('configuration.analysis.path_history.last', 'Last'), value: summary?.lastDate || '-' },
            { label: localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'Accuracy'), value: normalizedAccuracy }
          ]
        })
      });

      popup.open(map);
    });

    map.events.add('mouseout', layer, () => {
      popup.close();
    });

    const points = allAlignedData.map((point: any) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [point.coord[0], point.coord[1]]
      },
      properties: {
        seriesIndex: point.seriesIndex,
        pointIndex: point.pointIndex,
        pointColor: point.pathColor,
        deviceMac: point.deviceMac,
        date: point.date,
        lastPositionDatetime: point.lastPositionDatetime,
        localTime: point.localTime,
        likelihood: point.likelihood
      }
    }));

    const batchSize = Math.max(250, Math.ceil(points.length / 20));
    let currentIndex = 0;

    const addNextBatch = () => {
      if (allPointsRenderTokenRef.current !== renderToken) {
        return;
      }

      const batch = points.slice(currentIndex, currentIndex + batchSize);
      if (batch.length) {
        source.add(batch as any);
      }

      currentIndex += batch.length;

      if (currentIndex < points.length) {
        allPointsFrameRef.current = requestAnimationFrame(addNextBatch);
        return;
      }

      allPointsFrameRef.current = null;
      setAllPointsLoading(false);
    };

    allPointsFrameRef.current = requestAnimationFrame(addNextBatch);
  }, [allAlignedData, clearAllPointsLayer, countWakeups, localizationService, selectedDevicesLabel, signalsByDevice, seriesSummaryByDevice, buildPathHistoryTooltipHtml, parseDateLabel]);

  useEffect(() => {
    if (!mapReady || !showAllPoints || !allAlignedData.length) {
      clearAllPointsLayer();
      return;
    }

    showAllMarkers();

    return () => {
      clearAllPointsLayer();
    };
  }, [allAlignedData, clearAllPointsLayer, mapReady, showAllMarkers, showAllPoints]);


  const pathSliderStepIndex = Math.min(pathStep, Math.max(alignedData.length - 1, 0));
  const displayedFromDate = alignedData[0]?.date || fromDateUpdated || sliderFromDate;
  const displayedToDate = alignedData[pathSliderStepIndex]?.date || toDateUpdated || sliderToDate;

  useEffect(() => {
    if (!alignedData.length) {
      previousPathLengthRef.current = 0;
      return;
    }

    if (alignedData.length !== previousPathLengthRef.current) {
      const lastPathIndex = alignedData.length - 1;
      setPathStep(lastPathIndex);
      setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
      setToDateUpdated(alignedData[lastPathIndex]?.date || sliderToDate || '');
      updateLines(0, lastPathIndex);
      previousPathLengthRef.current = alignedData.length;
    }
  }, [alignedData, sliderFromDate, sliderToDate]);

  useEffect(() => {
    if (!showAllPoints || !alignedData.length) {
      return;
    }

    const lastPathIndex = alignedData.length - 1;

    if (pathStep !== lastPathIndex) {
      setPathStep(lastPathIndex);
    }

    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[lastPathIndex]?.date || sliderToDate || '');

    if (typeof onSliderDateChange === 'function') {
      onSliderDateChange(alignedData[lastPathIndex]?.date || sliderToDate || '');
    }

    updateLines(0, lastPathIndex);
  }, [alignedData, onSliderDateChange, pathStep, showAllPoints, sliderFromDate, sliderToDate]);

  useEffect(() => {
    if (!showSlider) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const clampedStep = Math.min(pathStep, alignedData.length - 1);
    if (clampedStep !== pathStep) {
      setPathStep(clampedStep);
      return;
    }

    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[clampedStep]?.date || sliderToDate || '');
  }, [alignedData, showSlider, sliderFromDate, sliderToDate, pathStep]);

  useEffect(() => {
    if (!hasExternalSliderControl) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const normalizedStartPercent = Math.max(0, Math.min(100, Number(externalSliderStartPercent ?? 0)));
    const normalizedEndPercent = Math.max(0, Math.min(100, Number(externalSliderPercent)));
    const startPercent = Math.min(normalizedStartPercent, normalizedEndPercent);
    const endPercent = Math.max(normalizedStartPercent, normalizedEndPercent);
    const targetStartIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round((startPercent / 100) * (alignedData.length - 1))));
    const targetEndIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round((endPercent / 100) * (alignedData.length - 1))));

    setPathStep(targetEndIndex);
    setFromDateUpdated(alignedData[targetStartIndex]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[targetEndIndex]?.date || sliderToDate || '');
    if (typeof onSliderDateChange === 'function') {
      onSliderDateChange(alignedData[targetEndIndex]?.date || sliderToDate || '');
    }
    updateLines(targetStartIndex, targetEndIndex);
  }, [externalSliderStartPercent, externalSliderPercent, hasExternalSliderControl, alignedData, sliderFromDate, sliderToDate, onSliderDateChange]);

  const updateLines = (startIndex: number, endIndex: number) => {
    if (!lineDataSourceRef.current) return;
    const map = mapRef.current;
    if (!map) return;

    const dataSource = lineDataSourceRef.current;
    dataSource.clear();

    const minIndex = Math.max(0, Math.min(alignedData.length - 1, startIndex));
    const maxIndex = Math.max(minIndex, Math.min(alignedData.length - 1, endIndex));
    const partialPoints = alignedData.slice(minIndex, maxIndex + 1).map((p: any) => p.coord);

    const features = buildPathSegmentFeatures(minIndex, maxIndex);
    if (features.length > 0) {
      dataSource.add(features as any);
    }

    const markerStates = getMarkerStates(maxIndex);

    if (markerStates.length > 0 && (showSlider || hasExternalSliderControl)) {
      clearPathMarkers();
      markerRefs.current = markerStates.map((state) => {
        const marker = createPathHistoryMarker(state.position, state.pathColor);
        map.markers.add(marker);
        initMarkerPopup(marker, state);
        return marker;
      });
    } else {
      clearPathMarkers();
    }

    if (markerRefs.current.length > 0 && partialPoints.length > 0) {
      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(alignedData[maxIndex]?.date || toDateUpdated || '');
      }
    }
  };

  const initMarkerPopup = (
    marker: atlas.HtmlMarker | null,
    markerState?: PathMarkerState
  ) => {
    if (!marker) return;

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -30], closeButton: false });
    }

    const tryAttachHover = () => {
      const el = marker?.getElement?.();
      if (!el) {
        setTimeout(tryAttachHover, 100);
        return;
      }

      if (el.dataset.hoverAttached) return;
      el.dataset.hoverAttached = 'true';

      el.onmouseenter = () => {
        const pointLocalTime = markerState?.pointData?.localTime || '';
        const pointDeviceMac = markerState?.deviceMac || selectedDevicesLabel;
        const signalsForDevice = markerState?.signalsCount ?? (markerState?.deviceMac ? (signalsByDevice[markerState.deviceMac] ?? countWakeups) : countWakeups);
        const accuracy = Number(markerState?.pointData?.likelihood);
        const normalizedAccuracy = Number.isFinite(accuracy) ? accuracy.toFixed(2) : '';
        const summary = seriesSummaryByDevice[pointDeviceMac];

        popupRef.current?.setOptions({
          position: markerState?.position,
          content: buildPathHistoryTooltipHtml({
            title: summary?.name && summary.name !== '-' ? summary.name : pointDeviceMac,
            accentColor: summary?.color || markerState?.pathColor,
            rows: [
              { label: nameLabel, value: summary?.name || '-' },
              { label: localizationService.toLanguageString('configuration.analysis.path_history.mac', 'Mac'), value: pointDeviceMac, mono: true },
              { label: signalLabel, value: `${(markerState?.pointIndex ?? 0) + 1}/${signalsForDevice}` },
              { label: localizationService.toLanguageString('configuration.analysis.path_history.distance', 'Distance'), value: summary?.distance || '-' },
              { label: localizationService.toLanguageString('configuration.analysis.path_history.first', 'First'), value: summary?.firstDate || '-' },
              { label: currentLabel, value: parseDateLabel(pointLocalTime) },
              { label: localizationService.toLanguageString('configuration.analysis.path_history.last', 'Last'), value: summary?.lastDate || '-' },
              { label: localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'Accuracy'), value: normalizedAccuracy }
            ]
          }),
        });

        if (popupRef.current && mapRef?.current) {
          popupRef.current.open(mapRef?.current);
        }
      };

      el.onmouseleave = () => {
        popupRef.current?.close();
      };
    };

    tryAttachHover();
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setPathStep(value);

    // update date
    if (alignedData.length > 0 && value >= 0) {
      const fromIdx = 0;
      const toIdx = Math.min(value, alignedData.length - 1);

      const from = alignedData[fromIdx]?.date || sliderFromDate;
      const to = alignedData[toIdx]?.date || sliderToDate;

      setFromDateUpdated(from);
      setToDateUpdated(to);
      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(to || '');
      }
    }

    updateLines(0, value);
  };

  const maxSlider = alignedData.length;

  const createPathHistoryMarker = (position: [number, number], fillColor: string = '#198692') => {
    return new atlas.HtmlMarker({
      position,
      //anchor: 'top',
      pixelOffset: [0, 0],
      htmlContent: `
        <span style="display:inline-block; width:22px; height:30px;">
          <svg viewBox="0 0 24 24" width="22" height="30" fill="${fillColor}" stroke="white" stroke-width="1.2" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 4.5 6 11 7 11s7-6.5 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
        </span>
      `
    });
  };
  
  const userInteractingRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onMouseDown = () => userInteractingRef.current = true;
    const onMoveEnd = () => setTimeout(() => userInteractingRef.current = false, 200);

    map.events.add('mousedown', onMouseDown);
    map.events.add('moveend', onMoveEnd);

    return () => {
      map.events.remove('mousedown', onMouseDown);
      map.events.remove('moveend', onMoveEnd);
    };
  }, []);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {allPointsLoading && loadingPanel}
      <button
        className='azure-reset-zoom'
        title={localizationService.toLanguageString('azure.reset_zoom', 'NOT_FOUND')}
        onClick={resetCenter}
      >
        <SvgIcon icon={currentLocation} className="icon" />
      </button>
      {maxPathLength > 0 && showSlider && !hasExternalSliderControl &&
        <div
          style={{
            position: "absolute",
            width: '150px',
            height: '100px',
            top: '10px',
            left: "50px",
            zIndex: 1000,
            background: 'white'
          }}
        >
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center'
          }}>
            {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
          </span>
          <input
            type="range"
            min="0"
            max={Math.max((maxSlider ?? 1) - 1, 0)}
            value={pathStep}
            onChange={handleSliderChange}
            style={{
              display: "block",
              margin: '0 auto',
              width: '80%',
              accentColor: '#198692'
            }}
          />
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {localizationService.toLanguageString('azure.from', 'NOT_FOUND')} - {localizationService.toLanguageString('azure.to', 'NOT_FOUND')}
          </span>
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {displayedFromDate && displayedToDate ? `${displayedFromDate} - ${displayedToDate}` : displayedFromDate || displayedToDate}
          </span>
        </div>
      }
      {pathLegendItems.length > 0 &&
        <div
          style={{
            position: 'absolute',
            right: '10px',
            bottom: '10px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.92)',
            border: '1px solid #d5e3f2',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(13, 45, 74, 0.12)',
            padding: '8px 10px',
            minWidth: '140px',
            maxWidth: '220px'
          }}
        >
          <div style={{
            fontSize: '11px',
            fontWeight: 700,
            color: '#0d2d4a',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.03em'
          }}>
            {localizationService.toLanguageString('configuration.analysis.path_history.path_history_title', 'Path history')}
          </div>
          {pathLegendItems.map((item) => (
            <div
              key={item.deviceMac}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px',
                opacity: hiddenDevices[item.deviceMac] ? 0.55 : 1
              }}
            >
              <span
                style={{
                  width: '18px',
                  height: '4px',
                  borderRadius: '999px',
                  backgroundColor: item.pathColor,
                  flexShrink: 0
                }}
              />
              <button
                type="button"
                onClick={() => toggleDeviceVisibility(item.deviceMac)}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  width: '100%'
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: '#11314f',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: hiddenDevices[item.deviceMac] ? 'line-through' : 'none'
                  }}
                >
                  {deviceNamesByMac?.[item.deviceMac] || item.deviceMac}
                </span>
                <SvgIcon icon={hiddenDevices[item.deviceMac] ? eyeSlashIcon : eyeIcon} />
              </button>
            </div>
          ))}
        </div>
      }
      <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
    </div>
  );

};